import { TaJson } from "ta-json";

import { Nullable, NullableResultPromise, type Optional } from "../base-types";
import { HEADERS } from "../constants/api";
import { ENTITIES } from "../constants/defaults";
import { IEntity } from "../contracts/base/entity";
import { EntityIterator, IEntityIterator } from "../contracts/querying/entity-iterator";
import { EntityLoadConfiguration, IEntityLoadConfiguration } from "../contracts/querying/entity-load-configuration";
import { IEntityQueryResult } from "../contracts/querying/entity-query-result";
import {
    ComparisonOperator,
    DefinitionQueryFilter,
    IdentifierQueryFilter,
    IdQueryFilter,
} from "../contracts/querying/filters";
import { EntityIdIterator, IIdIterator } from "../contracts/querying/id-iterator";
import { IIdQueryResult } from "../contracts/querying/id-query-result";
import { Query } from "../contracts/querying/query";
import { Sorting } from "../contracts/querying/sorting";
import CultureInfo from "../culture-info";
import ErrorMessages from "../error-messages";
import { InternalError } from "../errors/internal-error";
import { WebApiError } from "../errors/web-api-error";
import Guard from "../guard";
import Link from "../link";
import { EntityCopyOptionsMapper } from "../mappers/entity-copy-options-mapper";
import { IEntityCopyOptions } from "../models/content/entity-copy-options";
import { TypeGuards } from "../type-guards";
import { IExtendedContentHubClient } from "./extended-client";
import { CancelCallback } from "./internal-client";
import { ResponseHandler } from "./response-handler";

/**
 * Interface for the client responsible for getting, saving and deleting entities.
 *
 * @remarks
 * When possible, always include the specific properties and relations that are required when fetching entities.
 */
export interface IEntitiesClient {
    /**
     * Gets the {@link IEntity} instance with the specified `id`.
     * @param id - The id of the entity to get
     * @param loadConfiguration - The loading configuration for the entity
     * @param cancelCallback - A {@link CancelCallback} that will be placed in an Axios {@link CancelToken} if provided
     * @returns The entity or `null` when it was not found.
     */
    getAsync(
        id: number,
        loadConfiguration?: Nullable<IEntityLoadConfiguration>,
        cancelCallback?: CancelCallback
    ): NullableResultPromise<IEntity>;

    /**
     * Gets the {@link IEntity} instance with the specified `identifier`.
     * @param identifier - The identifier of the entity to get
     * @param loadConfiguration - The loading configuration for the entity
     * @param cancelCallback - A {@link CancelCallback} that will be placed in an Axios {@link CancelToken} if provided
     * @returns The entity or `null` when it was not found.
     */
    getAsync(
        identifier: string,
        loadConfiguration?: Nullable<IEntityLoadConfiguration>,
        cancelCallback?: CancelCallback
    ): NullableResultPromise<IEntity>;

    /**
     * Gets the {@link IEntity} instance with the specified `id` or `identifier`.
     * @param param - The id or identifier of the entity to get
     * @param loadConfiguration - The loading configuration for the entity
     * @param cancelCallback - A {@link CancelCallback} that will be placed in an Axios {@link CancelToken} if provided
     * @returns The entity or `null` when it was not found.
     */
    getAsync(
        param: number | string,
        loadConfiguration?: Nullable<IEntityLoadConfiguration>,
        cancelCallback?: CancelCallback
    ): NullableResultPromise<IEntity>;

    /**
     * Gets the {@link IEntity} instances with the specified `ids`.
     *
     * @remarks
     * The returned list of entities can be empty or contain fewer items than the list of ids.
     * The order of the entities in the returned list can be different than the order of the requested ids.
     *
     * @param ids - The ids of the entities to get
     * @param loadConfiguration - The loading configuration for an entity
     * @param cancelCallback - A {@link CancelCallback} that will be placed in an Axios {@link CancelToken} if provided
     * @returns List of entities.
     */
    getManyAsync(
        ids: Array<number>,
        loadConfiguration?: Nullable<IEntityLoadConfiguration>,
        cancelCallback?: CancelCallback
    ): Promise<Array<IEntity>>;

    /**
     * Gets the {@link IEntity} instances with the specified `identifiers`.
     *
     * @remarks
     * The returned list of entities can be empty or contain fewer items than the list of identifiers.
     * The order of the entities in the returned list can be different than the order of the requested identifiers.
     *
     * @param identifiers - The identifiers of the entities to get
     * @param loadConfiguration - The loading configuration for an entity
     * @param cancelCallback - A {@link CancelCallback} that will be placed in an Axios {@link CancelToken} if provided
     * @returns List of entities.
     */
    getManyAsync(
        identifiers: Array<string>,
        loadConfiguration?: Nullable<IEntityLoadConfiguration>,
        cancelCallback?: CancelCallback
    ): Promise<Array<IEntity>>;

    /**
     * Gets the {@link IEntity} instances with the specified `ids` or `identifiers`.
     *
     * @remarks
     * The returned list of entities can be empty or contain fewer items than the list of identifiers.
     * The order of the entities in the returned list can be different than the order of the requested identifiers.
     *
     * @param param - The ids or identifiers of the entities to get
     * @param loadConfiguration - The loading configuration for an entity
     * @param cancelCallback - A {@link CancelCallback} that will be placed in an Axios {@link CancelToken} if provided
     * @returns List of entities.
     */
    getManyAsync(
        param: Array<number> | Array<string>,
        loadConfiguration?: Nullable<IEntityLoadConfiguration>,
        cancelCallback?: CancelCallback
    ): Promise<Array<IEntity>>;

    /**
     * Queries the {@link IEntity} instances for the specified `definitionName`.
     *
     * @param definitionName - The name of the definition to get entities for (case insensitive)
     * @param loadConfiguration - The loading configuration for an entity
     * @param skip - Skip the specified number of entities (optional)
     * @param take - Take only the specified number of entities (optional, default: {@link ENTITIES.take})
     * @param sorting - Sorting options
     * @param cancelCallback - A {@link CancelCallback} that will be placed in an Axios {@link CancelToken} if provided
     * @returns List of entities.
     */
    getByDefinitionAsync(
        definitionName: string,
        loadConfiguration?: Nullable<IEntityLoadConfiguration>,
        skip?: number,
        take?: number,
        sorting?: Array<Sorting>,
        cancelCallback?: CancelCallback
    ): Promise<IEntityQueryResult>;

    /**
     * Queries the {@link IEntity} ids for the specified `definitionName`.
     *
     * @param definitionName - The name of the definition to get entity ids for (case insensitive)
     * @param skip - Skip the specified number of entities (optional)
     * @param take - Take only the specified number of entities (optional, default: {@link ENTITIES.take})
     * @param cancelCallback - A {@link CancelCallback} that will be placed in an Axios {@link CancelToken} if provided
     * @returns List of entity ids.
     */
    getIdsByDefinitionAsync(
        definitionName: string,
        skip?: number,
        take?: number,
        cancelCallback?: CancelCallback
    ): Promise<IIdQueryResult>;

    /**
     * Creates an {@link IEntityIterator} instance for the specified definition that returns instances.
     * Results are sorted ascending on creation date.
     *
     * @param definition - The name of the definition to get entities for
     * @param loadConfiguration - Load configuration
     * @returns The iterator.
     */
    getEntityIterator(definition: string, loadConfiguration?: Nullable<IEntityLoadConfiguration>): IEntityIterator;

    /**
     * Creates an {@link IIdIterator} instance for the specified definition that returns instances.
     * Results are sorted ascending on creation date.
     *
     * @param definition - The name of the definition to get entity ids for
     * @returns The iterator.
     */
    getEntityIdIterator(definition: string): IIdIterator;

    /**
     * Save the specified {@link IEntity}.
     *
     * @remarks
     * The existing {@link IEntity} will not be updated in any way.
     * If any information is needed that was generated on the server after persisting, the entity will need to be
     * fetched again.
     *
     * @param entity - Entity to save
     * @param cancelCallback - A {@link CancelCallback} that will be placed in an Axios {@link CancelToken} if provided
     * @param culture - The culture to update the entity in
     * @returns The id of the entity.
     */
    saveAsync(entity: IEntity, cancelCallback?: CancelCallback, culture?: CultureInfo): Promise<number>;

    /**
     * Deletes the {@link IEntity} with specified id.
     *
     * @param entityId - The id of the entity to delete
     * @param cancelCallback - A {@link CancelCallback} that will be placed in an Axios {@link CancelToken} if provided
     */
    deleteAsync(entityId: number, cancelCallback?: CancelCallback): Promise<void>;

    /**
     * Copies an {@link IEntity} by id to a new or existing entity.
     *
     * @param entityId - The id of the entity to delete
     * @param copyOptions - The copy options.
     *
     * @returns The id of the copied entity.
     * @param cancelCallback - A {@link CancelCallback} that will be placed in an Axios {@link CancelToken} if provided
     */
    copyAsync(entityId: number, copyOptions: IEntityCopyOptions, cancelCallback?: CancelCallback): Promise<number>;
}

/**
 * The client responsible for getting, saving and deleting entities.
 */
export class EntitiesClient implements IEntitiesClient {
    private readonly _client: IExtendedContentHubClient;

    constructor(client: IExtendedContentHubClient) {
        Guard.notNullOrUndefined(client);

        this._client = client;
    }

    /**
     * {@inheritDoc}
     */
    async getAsync(
        param: string | number,
        loadConfiguration?: Nullable<IEntityLoadConfiguration>,
        cancelCallback?: CancelCallback
    ): NullableResultPromise<IEntity> {
        let query: Query;
        if (typeof param === "string") {
            Guard.stringNotNullOrEmpty(param);

            query = new Query({
                filter: new IdentifierQueryFilter({
                    operator: ComparisonOperator.Equals,
                    value: param,
                }),
            });
        } else {
            Guard.validId(param);

            query = new Query({
                filter: new IdQueryFilter({
                    operator: ComparisonOperator.Equals,
                    value: param,
                }),
            });
        }

        const result = await this._client.querying.queryAsync(query, loadConfiguration || null, cancelCallback);
        const entity = EntitiesClient.single(result);

        return entity;
    }

    /**
     * {@inheritDoc}
     */
    async getManyAsync(
        param: Array<number> | Array<string>,
        loadConfiguration?: Nullable<IEntityLoadConfiguration>,
        cancelCallback?: CancelCallback
    ): Promise<Array<IEntity>> {
        if (param == null || param.length === 0) return [];

        let query: Query;
        if (TypeGuards.isStringArray(param)) {
            const identifiers: Array<string> = param;
            Guard.arrayNoneNullOrEmptyString(identifiers);

            query = new Query({
                filter: new IdentifierQueryFilter({
                    operator: ComparisonOperator.Equals,
                    values: identifiers,
                }),
            });
        } else {
            const ids: Array<number> = param;
            Guard.arrayNoneNullOrUndefined(ids);
            Guard.validIds(ids);

            query = new Query({
                filter: new IdQueryFilter({
                    operator: ComparisonOperator.Equals,
                    values: ids,
                }),
            });
        }

        const result = await this._client.querying.queryAsync(query, loadConfiguration || null, cancelCallback);

        return result.items;
    }

    /**
     * {@inheritDoc}
     */
    async getByDefinitionAsync(
        definitionName: string,
        loadConfiguration?: Nullable<IEntityLoadConfiguration>,
        skip: number = ENTITIES.skip,
        take: number = ENTITIES.take,
        sorting?: Array<Sorting>,
        cancelCallback?: CancelCallback
    ): Promise<IEntityQueryResult> {
        Guard.stringNotNullOrEmpty(definitionName);
        Guard.notNegative(skip);
        Guard.notNegative(take);

        const query = new Query({
            filter: new DefinitionQueryFilter({
                name: definitionName,
                operator: ComparisonOperator.Equals,
            }),
            skip: skip,
            take: take,
            sorting: sorting,
        });

        return this._client.querying.queryAsync(query, loadConfiguration, cancelCallback);
    }

    /**
     * {@inheritDoc}
     */
    async getIdsByDefinitionAsync(
        definitionName: string,
        skip?: number,
        take?: number,
        cancelCallback?: CancelCallback
    ): Promise<IIdQueryResult> {
        Guard.stringNotNullOrEmpty(definitionName);
        if (skip != null) Guard.notNegative(skip);
        if (take != null) Guard.notNegative(take);

        const query = new Query({
            filter: new DefinitionQueryFilter({
                name: definitionName,
                operator: ComparisonOperator.Equals,
            }),
            skip: skip,
            take: take,
            //TODO sorting on created on ascending?
        });

        const result = await this._client.querying.queryIdsAsync(query, cancelCallback);

        return result;
    }

    /**
     * {@inheritDoc}
     */
    getEntityIterator(definition: string, loadConfiguration?: Nullable<IEntityLoadConfiguration>): IEntityIterator {
        Guard.stringNotNullOrEmpty(definition);
        loadConfiguration = loadConfiguration || EntityLoadConfiguration.Default;

        const query = new Query({
            filter: new DefinitionQueryFilter({
                name: definition,
                operator: ComparisonOperator.Equals,
            }),
        });

        return new EntityIterator(this._client, query, loadConfiguration);
    }

    /**
     * {@inheritDoc}
     */
    getEntityIdIterator(definition: string): IIdIterator {
        Guard.stringNotNullOrEmpty(definition);

        const query = new Query({
            filter: new DefinitionQueryFilter({
                name: definition,
                operator: ComparisonOperator.Equals,
            }),
        });

        return new EntityIdIterator(this._client, query);
    }

    /**
     * {@inheritDoc}
     */
    async saveAsync(entity: IEntity, cancelCallback?: CancelCallback, culture?: CultureInfo): Promise<number> {
        Guard.notNullOrUndefined(entity);

        let id: number;
        if (entity.isNew) {
            id = await this.createEntityAsync(entity, cancelCallback);
        } else {
            await this.updateEntityAsync(entity, cancelCallback, culture);
            id = entity.id!;
        }

        entity.markClean();

        return id;
    }

    /**
     * {@inheritDoc}
     */
    async deleteAsync(entityId: number, cancelCallback?: CancelCallback): Promise<void> {
        Guard.validId(entityId);

        const link = await this._client.linkHelper.entityToLinkAsync(entityId);
        const response = await this._client.raw.deleteAsync(link.href, undefined, cancelCallback);

        ResponseHandler.handleErrors(response);
    }

    /**
     * {@inheritDoc}
     */
    async copyAsync(
        entityId: number,
        copyOptions: IEntityCopyOptions,
        cancelCallback?: CancelCallback
    ): Promise<number> {
        Guard.validId(entityId);
        Guard.notNull(copyOptions);

        const link = await this._client.linkHelper.copyEntityToLinkAsync(entityId);

        const options = EntityCopyOptionsMapper.map(copyOptions);

        const response = await this._client.raw.postAsync(
            link.href,
            TaJson.serialize(options),
            undefined,
            cancelCallback
        );
        ResponseHandler.handleErrors(response);

        const href = response.responseHeaders[HEADERS.location];
        if (!href) {
            throw new WebApiError(
                ErrorMessages.EntitiesClient.UnableToExtractLocationHeaderFromResponse,
                response.statusCode,
                response
            );
        }

        const id = await this._client.linkHelper.idFromEntityAsync(new Link(href));
        if (id === null || id === 0) {
            throw new InternalError(ErrorMessages.EntitiesClient.UnableToExtractEntityIdFromHeader);
        }

        return id;
    }

    /**
     * Creates the specified {@link IEntity} in the system.
     *
     * @param entity - The entity to create/save in the system
     */
    private async createEntityAsync(entity: IEntity, cancelCallback?: CancelCallback): Promise<number> {
        const link = await this._client.linkHelper.entitiesLinkAsync();
        const resource = await this._client.entityMapper.toResourceAsync(entity);

        const response = await this._client.raw.postAsync(
            link.href,
            TaJson.serialize(resource),
            undefined,
            cancelCallback
        );

        ResponseHandler.handleErrors(response);

        const href = response.responseHeaders[HEADERS.location];

        if (!href) {
            throw new WebApiError(
                ErrorMessages.EntitiesClient.UnableToExtractLocationHeaderFromResponse,
                response.statusCode,
                response
            );
        }

        const id = await this._client.linkHelper.idFromEntityAsync(new Link(href));
        if (id === null || id === 0) {
            throw new InternalError(ErrorMessages.EntitiesClient.UnableToExtractEntityIdFromHeader);
        }

        return id;
    }

    /**
     * Update the specified {@link IEntity}.
     *
     * @param entity - The entity to update
     * @param cancelCallback - A {@link CancelCallback} that will be placed in an Axios {@link CancelToken} if provided
     * @param cultureParam - The culture to update the entity in
     */
    private async updateEntityAsync(
        entity: IEntity,
        cancelCallback?: CancelCallback,
        cultureParam?: CultureInfo
    ): Promise<void> {
        // Check if the entity is loaded in only one culture to improve save performance.
        let culture: Optional<CultureInfo> = cultureParam;
        if (entity.cultures.length === 1) {
            culture = entity.cultures[0];
        }

        const link = await this._client.linkHelper.entityToLinkAsync(entity.id!, culture ?? undefined);
        const resource = await this._client.entityMapper.mapDirtiesToResourceAsync(entity);
        const response = await this._client.raw.putAsync(
            link.href,
            TaJson.serialize(resource),
            undefined,
            cancelCallback
        );

        ResponseHandler.handleErrors(response);
    }

    /**
     * Get an {@link IEntity} or `null` from the specified {@link IEntityQueryResult}.
     *
     * @remarks
     * Throws an {@link InternalError} if multiple results where available.
     *
     * @param queryResult - The query result (should contain one or no items)
     * @returns An {@link IEntity} or `null`.
     */
    private static single(queryResult: IEntityQueryResult): Nullable<IEntity> {
        if (queryResult.totalNumberOfResults === 0) {
            return null;
        } else if (queryResult.totalNumberOfResults === 1) {
            return queryResult.items[0];
        } else {
            throw new InternalError(ErrorMessages.QueryingClient.MultipleResultsWhenOneExpected);
        }
    }
}
